import { StyleProp, View, ViewStyle } from "react-native";
import {
    Headline,
    IconButton,
    NumpadConfirmButton,
    PinPad,
    StyleFunction,
    useThemedStyle,
} from "../../";
import React, { useState } from "react";
import { useTranslation } from "locales";

export function Pin({
    alertMessage,
    onSubmit,
    onClear,
    headerText,
    hideCloseButton,
    style,
}: {
    onSubmit: (pinValue: string) => void;
    onClear?: () => void;
    alertMessage?: string;
    headerText?: string;
    hideCloseButton?: boolean;
    style?: StyleProp<ViewStyle>;
}) {
    const [t] = useTranslation();
    const styles = useThemedStyle(styleFunc);
    const [numPadValue, setNumPadValue] = useState<string>("");

    const disabledButton = numPadValue.length === 0;

    return (
        <View style={styles.flex}>
            <View style={styles.header}>
                {headerText ? (
                    <Headline
                        size="h2"
                        style={[
                            styles.headlineText,
                            hideCloseButton ? null : styles.hasCloseButton,
                        ]}
                    >
                        {headerText}
                    </Headline>
                ) : (
                    <View />
                )}
                {hideCloseButton || !onClear ? null : (
                    <View style={styles.icon}>
                        <IconButton
                            size="large"
                            color={styles.icon.color}
                            name="times-circle"
                            onPress={onClear}
                        />
                    </View>
                )}
            </View>
            <View style={[styles.container, style]}>
                <PinPad
                    value=""
                    onValueChange={setNumPadValue}
                    maxLength={10}
                    showDisplay={true}
                    maskInput={true}
                    alertMessage={alertMessage}
                />
                <NumpadConfirmButton
                    onPress={() => onSubmit(numPadValue)}
                    text={t("common.numpad.ok", "OK")}
                    buttonStyle={[
                        styles.confirmButton,
                        disabledButton ? styles.disabledButton : null,
                    ]}
                    textStyle={[
                        styles.confirmButtonText,
                        disabledButton ? styles.disabledButtonText : null,
                    ]}
                    disabled={disabledButton}
                    testID="pin:ok"
                />
            </View>
        </View>
    );
}

const styleFunc: StyleFunction = (theme, dimensions) => ({
    flex: {
        ...theme.styles.flex1,
    },

    container: {
        ...theme.styles.flex1,

        alignSelf: "center",
        marginBottom: theme.spacingScale * 3,

        maxWidth:
            dimensions.width <= theme.breakpoint.portraitWidth ? "100%" : 400,
        width: "100%",
        minWidth:
            dimensions.width <= theme.breakpoint.portraitWidth ? "100%" : 250,
    },
    confirmButton: {
        backgroundColor: theme.colors.secondary,
    },
    confirmButtonText: {
        color: theme.colors.textLight,
        fontWeight: "bold",
    },
    icon: {
        elevation: 20,
        color: theme.colors.white,
    },
    hasCloseButton: {
        // Adding the space of the Close iconbutton to the left side of the headline text, so the headline text remains centered if there's a Close button.
        marginLeft:
            dimensions.width <= theme.breakpoint.portraitWidth ? 16 : 56,
    },
    header: {
        flexDirection: "row",
        alignItems: "stretch",
        justifyContent: "space-between",
        paddingRight: theme.spacingScale,
        paddingTop: theme.spacingScale,
    },
    headlineText: {
        flex: 1,
        color: theme.colors.textLight,
        textAlign:
            dimensions.width < theme.breakpoint.portraitWidth
                ? "left"
                : "center",
        marginLeft: theme.spacingScale,
    },
    disabledButton: {
        backgroundColor: theme.colors.grey100,
    },
    disabledButtonText: {
        color: theme.colors.grey250,
    },
});
